import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Category {
  id: number;
  name: string;
  description?: string;
}

const AdminCategories: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/categories`, {
          credentials: "include",
        });
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [API_URL]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading Categories...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-semibold uppercase text-gray-900">
          Categories
        </h2>
        <button
          onClick={() => navigate("/categories/new")}
          className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium uppercase text-white hover:bg-green-700"
        >
          + Add Category
        </button>
      </div>

      {/* Categories Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-300 bg-white shadow-sm">
        <table className="min-w-full border-collapse text-left text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border-b border-gray-300 px-4 py-3 font-medium text-gray-700">
                Name
              </th>
              <th className="border-b border-gray-300 px-4 py-3 font-medium text-gray-700">
                Description
              </th>
              <th className="border-b border-gray-300 px-4 py-3 text-right font-medium text-gray-700">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {categories.length ? (
              categories.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="border-b border-gray-300 px-4 py-3 font-medium text-gray-900">
                    {c.name}
                  </td>

                  <td className="border-b border-gray-300 px-4 py-3 text-gray-600">
                    {c.description ? (
                      c.description.length > 60
                        ? c.description.slice(0, 60) + "…"
                        : c.description
                    ) : (
                      <span className="italic text-gray-400">
                        No description
                      </span>
                    )}
                  </td>

                  <td className="border-b border-gray-300 px-4 py-3 text-right">
                    <button
                      onClick={() =>
                        navigate(`/categories/edit/${c.id}`)
                      }
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium uppercase text-gray-700 hover:bg-gray-100"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  No categories available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCategories;
